import type {
  FlowFilterCondition,
  FlowFilterNodeConfig,
  FlowFilterOp,
  TemplateContext,
} from '../types/index.js';
import {
  resolvePath,
  resolveTemplate,
  isTruthyResolvedString,
  isTruthyValue,
  extractTemplatePaths,
  type TemplateRoot,
} from './template-engine.js';

/**
 * Roots that carry data when a trigger's embedded filter evaluates. Embedded
 * filters gate run creation at dispatch, before any run exists, against
 * {@link triggerFilterContext} — only the event payload (as both `trigger`
 * and `input`).
 */
export const TRIGGER_FILTER_ROOTS = [
  'trigger',
  'input',
] as const satisfies ReadonlyArray<TemplateRoot>;

/** Membership form of {@link TRIGGER_FILTER_ROOTS} for path-root checks. */
export const TRIGGER_FILTER_ROOT_SET: ReadonlySet<string> = new Set(TRIGGER_FILTER_ROOTS);

/**
 * The context a trigger's embedded filter evaluates against at dispatch.
 * Only {@link TRIGGER_FILTER_ROOTS} carry data; `state` is populated but
 * always empty, so a filter that reads it (e.g. a bare `{{state}}`
 * expression) sees `{}` rather than an absent root.
 */
export function triggerFilterContext(payload: Record<string, unknown>): TemplateContext {
  return { trigger: payload, input: payload, state: {} };
}

/**
 * Every context path a filter config reads: condition paths, or the
 * expression's placeholders.
 */
export function collectFilterPaths(config: FlowFilterNodeConfig): string[] {
  if (config.mode === 'expression') return extractTemplatePaths(config.expression);
  return (config.conditions ?? []).map((c) => c.path);
}

/**
 * Ops that test the resolved value itself instead of comparing it to a
 * `value` operand. Two consequences follow, and both are contractual — the
 * flow editor reads this set to decide which condition inputs to render:
 *
 * - they take no operand, and
 * - {@link evaluateCondition} answers them before the `ifMissing`
 *   short-circuit, so `ifMissing` never affects their outcome.
 */
export const PRESENCE_FILTER_OPS: ReadonlySet<FlowFilterOp> = new Set<FlowFilterOp>([
  'exists',
  'not_exists',
  'truthy',
]);

export interface FilterEvaluation {
  matched: boolean;
  /** Conditions that did not hold (empty when matched, or in expression mode). */
  failed: Array<{ path: string; op: string }>;
}

/**
 * Evaluate a filter config against the run context. A filter is exactly one of
 * two shapes, picked by `mode`:
 *
 * - `conditions`: each condition resolves its `path` through {@link resolvePath}
 *   (values keep their raw types, so arrays stay arrays for `in`/`intersects`),
 *   then they combine per `match` (`all` default / `any`). Zero conditions pass.
 * - `expression`: the template resolves and must be truthy.
 */
export function evaluateFilter(
  config: FlowFilterNodeConfig,
  context: TemplateContext,
): FilterEvaluation {
  if (config.mode === 'expression') {
    const matched = isTruthyResolvedString(resolveTemplate(config.expression, context));
    return { matched, failed: [] };
  }

  const conditions = config.conditions ?? [];
  const failed: Array<{ path: string; op: string }> = [];

  for (const condition of conditions) {
    if (!evaluateCondition(condition, context)) {
      failed.push({ path: condition.path, op: condition.op });
    }
  }

  const match = config.match ?? 'all';
  let matched: boolean;
  if (conditions.length === 0) {
    matched = true;
  } else if (match === 'any') {
    matched = failed.length < conditions.length;
  } else {
    matched = failed.length === 0;
  }

  return { matched, failed };
}

function evaluateCondition(condition: FlowFilterCondition, context: TemplateContext): boolean {
  const raw = resolvePath(condition.path, context);

  // Presence tests answer for themselves on a missing path, so they run
  // before the `ifMissing` short-circuit below — `not_exists` is exactly the
  // condition that must hold when the path resolves to nothing. Keep this
  // block in step with PRESENCE_FILTER_OPS; a test pins the two together.
  if (condition.op === 'exists') return raw !== undefined && raw !== null;
  if (condition.op === 'not_exists') return raw === undefined || raw === null;
  if (condition.op === 'truthy') return isTruthyValue(raw);

  // Missing values short-circuit per the condition's `ifMissing` policy —
  // this is what preserves the legacy per-provider filter semantics.
  if (raw === undefined || raw === null) return condition.ifMissing === 'pass';

  switch (condition.op) {
    case 'equals':
      return compareScalars(raw, condition.value, condition.caseInsensitive);
    case 'not_equals':
      return !compareScalars(raw, condition.value, condition.caseInsensitive);
    case 'contains': {
      // String containment, or array membership when the value is an array.
      if (Array.isArray(raw)) {
        return raw.some((item) => compareScalars(item, condition.value, condition.caseInsensitive));
      }
      if (typeof raw !== 'string') return false;
      const needle = String(condition.value ?? '');
      return condition.caseInsensitive
        ? raw.toLowerCase().includes(needle.toLowerCase())
        : raw.includes(needle);
    }
    case 'in': {
      const allowed = Array.isArray(condition.value) ? condition.value : [condition.value];
      return allowed.some((v) => compareScalars(raw, v, condition.caseInsensitive));
    }
    case 'intersects': {
      // Any element of the (array) value at `path` appears in the allow-list.
      const allowed = Array.isArray(condition.value) ? condition.value : [condition.value];
      const items = Array.isArray(raw) ? raw : [raw];
      return items.some((item) =>
        allowed.some((v) => compareScalars(item, v, condition.caseInsensitive)),
      );
    }
  }
}

function compareScalars(left: unknown, right: unknown, caseInsensitive?: boolean): boolean {
  if (typeof left === 'string' && typeof right === 'string') {
    return caseInsensitive ? left.toLowerCase() === right.toLowerCase() : left === right;
  }
  if (typeof left === 'number' && typeof right === 'number') return left === right;
  if (typeof left === 'boolean' && typeof right === 'boolean') return left === right;
  // Cross-type comparison: stringify both sides so `path: 42` matches
  // `value: '42'` (filter values authored in the UI arrive as strings).
  return String(left) === String(right);
}
